import { create } from 'zustand';

/**
 * Theme system. The active theme is reflected as a `data-theme` attribute
 * on <html>; all colors resolve from CSS variables defined per-theme in
 * src/index.css. A no-FOUC boot script in index.html sets the attribute
 * before first paint — this store keeps React in sync and persists changes.
 *
 * Adding a new theme: add a `[data-theme="x"]` block in index.css, add the
 * name to the `Theme` union here, and add a button in ThemeToggle.tsx.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'tg-theme';

function readStored(): Theme {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t === 'dark' || t === 'light') return t;
  } catch {
    /* ignore */
  }
  return 'light';
}

function applyTheme(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readStored(),
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  toggle: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },
}));
